// Resources
import React, {useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import useAPI from "../../../../hooks/useAPI";
import usePage from "../../../../hooks/usePage";

// Components
import Spinner from "../../../plugins/Spinner";
import {sendToast, questionToast} from "../../../plugins/toastAll";
import { SCROLL_CLASS, SCROLL_CSS, useFillHeight } from "./BotCard";
import { BOT_LIST_CSS, BotListCard, ConfirmDialog, ListPager, NoBots, NoResults, TYPE_COLORS, useFullBleed } from "./BotListKit";
import NewBotModal from "./NewBotModal";
import { ConfigDrawer, ConfigSection, SuSelect, SwitchRow } from "./BotConfigKit";

// Icons
import { LuClock, LuHeadphones, LuHome, LuMessageSquare, LuPlus, LuRepeat, LuSearch, LuSlidersHorizontal, LuSparkles, LuZap } from "react-icons/lu";
import { createDefaultAgentConfig } from "../Agent/agentCatalog";

const initialNodes = [
    {
      id: '1-basicAnswer',
      type: 'basicAnswer',
      data: { 
        title: 'Saludo Inicial',
        first: true,
        bot_response: "¡Hola! ¿Cómo puedo ayudarte hoy?",
        user_response: null,
        options: [
          {
            label: 'Conversar con agente',
            id: '1-basicAnswer-1-conversar-con-agente',
            name: 'agente',
          },
          {
            label: 'Continuar con Bot',
            id: '1-basicAnswer-2-continuar-con-bot',
            name: 'Bot',
          },
        ],
        file: null,
        saved: true,
        variables: null,
        color: '#00A884',
        advanced: {
          isAcceptTerms: null,
          campaign: null,
          campaign_options: [],
          request_name: null,
          confirm_name: null,
          edit_name: null,
        },
      },
      position: { x: 250, y: 5 }
    },
];

const normalizeText = (text) =>
    String(text || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const List = ({
    handleSearch,
    handlEditChatBot,
    handleCreateFromModal,
    getBots,
    getBotDetails,
    validation,
    listBots,
    listInactivity,
    listCompanyScheduleOptions,
    listAttempts,
    listAreas,
    listAdvisers,
    listBotsTemplates,
    isDark,
    customStyles,
    customStylesDark,
    mode,
    handleModalBotAssistant
}) => {
    const { urlAPI_3, fetchWithAuth } = useAPI(); 
    const { page, setPage, byPage, resetPage, setByPage } = usePage();
    const [listRootRef, listRootHeight] = useFillHeight(0);
    useFullBleed(listRootRef);
    const {watch,register,handleSubmit,formState: { errors },reset,} = useForm({
        defaultValues: {
            inactivityMessages: null,
            controlMessage: null,
            waitingMessageSOS: null,
            listSOSMessages: null,
            listExitIAMessages: null,
            waitingMessageExitIA: null
        },
    });

    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [configBot, setConfigBot] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [typeFilter, setTypeFilter] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const closeConfirm = useCallback(() => setConfirm(null), []);

    const obsoleteButtonTimeoutOptions = Array.from({ length: 30 }, (_, index) => {
        const minutes = index + 1;
        return {
            label: `${minutes} minuto${minutes > 1 ? 's' : ''}`,
            value: minutes,
        };
    });

    const debounceWindowOptions = [
        { label: 'Desactivado', value: 0 },
        { label: '1 segundo', value: 1000 },
        { label: '2 segundos', value: 2000 },
        { label: '3 segundos', value: 3000 },
        { label: '5 segundos', value: 5000 },
    ];
    const defaultDebounceWindow = debounceWindowOptions[1];

    // ------> Selects, Radio, Checkbox
    const [transferSOSType, setTransferSOSType] = useState(false);
    const [inactivityStatus, setInactivityStatus] = useState(false);
    const [obsoleteButtonStatus, setObsoleteButtonStatus] = useState(false);
    const [attemptsSOS, setAttemptsSOS] = useState(null);
    const [inactivityTime, setInactivityTime] = useState(null);
    const [inactivityTimeClose, setInactivityTimeClose] = useState(null);
    const [transferSOS, setTransferSOS] = useState(null);
    const [cordialityMessage, setCordialityMessage] = useState(false);
    const [activeSOSTab, setActiveSOSTab] = useState('transfer');
    const [obsoleteButtonTimeout, setObsoleteButtonTimeout] = useState(obsoleteButtonTimeoutOptions[4]);
    const [debounceWindowMs, setDebounceWindowMs] = useState(defaultDebounceWindow);
    const [activeSection, setActiveSection] = useState('response');

    // ------> Modals
    const [selectedTemplateModal, setSelectedTemplateModal] = useState(false);
    const [configModal, setConfigModal] = useState(false);
    const [configSnapshot, setConfigSnapshot] = useState(null);
    
    // Functions

    //# Opciones para filtros
    const statusChips = [
        { label: 'Activo', value: true, color: '#2fae4e' },
        { label: 'Inactivo', value: false, color: '#8B8B8B' }
    ];

    const typeChips = [
        ...Object.entries(TYPE_COLORS).map(([value, type]) => ({ value, label: type.label, color: type.color })),
        { value: 'agent', label: 'Agente IA', color: '#7563D8' }
    ];

    const setAgentConfig = (patch) => {
        setConfigBot((prev) => ({
            ...prev,
            agentConfig: { ...(prev?.agentConfig || { ...createDefaultAgentConfig(), enabled: false }), ...patch },
        }));
    };

    //# Function para contar filtros aplicados
    const getAppliedFiltersCount = () => {
        let count = 0;
        if (statusFilter !== null) count++;
        if (typeFilter !== null) count++;
        return count;
    };

    //# Function para limpiar filtros
    const clearFilters = () => {
        setStatusFilter(null);
        setTypeFilter(null);
    };
    const getFilteredBots = () => {
        let filtered = listBots;
        const query = normalizeText(search.trim());

        if (query) {
            filtered = filtered.filter(bot => normalizeText(bot.name).includes(query));
        }

        if (statusFilter !== null) {
            filtered = filtered.filter(bot => Boolean(bot.enabled) === statusFilter);
        }

        if (typeFilter === 'agent') {
            filtered = filtered.filter(bot => bot.agentConfig?.enabled);
        } else if (typeFilter !== null) {
            filtered = filtered.filter(bot => bot.type === typeFilter);
        }

        return filtered;
    };
    const visibleBots = getFilteredBots();

    //# Function para validar palabras duplicadas
    const validateDuplicateWords = (sosWords, exitWords) => {
        if (!sosWords || !exitWords) return { isValid: true, duplicates: [] };
        
        const sosArray = sosWords.split(',').map(word => word.trim().toLowerCase()).filter(word => word);
        const exitArray = exitWords.split(',').map(word => word.trim().toLowerCase()).filter(word => word);
        
        const duplicates = sosArray.filter(word => exitArray.includes(word));
        
        return {
            isValid: duplicates.length === 0,
            duplicates: duplicates
        };
    };

    const formValues = watch();
    const configSignature = JSON.stringify({
        form: formValues,
        attemptsSOS: attemptsSOS?.value ?? attemptsSOS ?? null,
        inactivityStatus: Boolean(inactivityStatus),
        obsoleteButtonStatus: Boolean(obsoleteButtonStatus),
        cordialityMessage: Boolean(cordialityMessage),
        inactivityTime: inactivityTime?.value ?? null,
        inactivityTimeClose: inactivityTimeClose?.value ?? null,
        transferSOS: transferSOS?.value ?? null,
        transferSOSType: transferSOSType || null,
        obsoleteButtonTimeout: obsoleteButtonTimeout?.value ?? null,
        debounceWindowMs: debounceWindowMs?.value ?? null,
        campaignFlows: Boolean(configBot?.campaignFlows),
        agentConfig: configBot?.agentConfig ?? null,
    }, (key, value) => (value === null || value === "" ? undefined : value));
    const configDirty = configModal && configSnapshot !== null && configSnapshot !== configSignature;

    const closeConfig = () => {
        reset({
            inactivityMessages: null,
            controlMessage: null,
            waitingMessageSOS: null,
            listSOSMessages: null,
            listExitIAMessages: null,
            waitingMessageExitIA: null
        });
        setConfigSnapshot(null);
        setConfigModal(false);
    };

    const requestCloseConfig = () => {
        if (!configDirty) {
            closeConfig();
            return;
        }
        setConfirm({
            title: 'Descartar los cambios',
            text: 'Tienes cambios sin guardar en la configuración de este bot.',
            action: 'Descartar',
            run: closeConfig,
        });
    };

    //# Function para manejar apertura de modal de configuraciones
    const openConfig = async (bot) => {
        setLoading(true);
        try {
            const fullBot = await getBotDetails(bot._id, { forceRefresh: true });
            if (!fullBot) {
                sendToast({ type: 'error', message: 'No fue posible cargar el bot.' });
                return;
            }

            const objectData = {
                inactivityMessages: fullBot.inactivityMessages?.length ? fullBot.inactivityMessages[0]?.text?.body : null,
                companySchedule: listCompanyScheduleOptions.find(v => v.value == fullBot.companySchedule) || null,
                controlMessage: fullBot.controlMessage ?? null,
                waitingMessageSOS: fullBot.waitingMessageSOS ?? null,
                listSOSMessages: fullBot.listSOSMessages ?? null,
                listExitIAMessages: fullBot.listExitIAMessages ?? null,
                waitingMessageExitIA: fullBot.waitingMessageExitIA ?? null,
                listCordialityMessages: fullBot.listCordialityMessages ?? null,
                responseCordialityMessage: fullBot.responseCordialityMessage ?? null,
            };
            setAttemptsSOS(fullBot.attemptsSOS ?? null);
            setInactivityStatus(fullBot.inactivityStatus || null);
            setObsoleteButtonStatus(fullBot.obsoleteButtonStatus ?? false);
            setCordialityMessage(fullBot.cordialityMessage || false)
            setInactivityTime(listInactivity.find(v => v.value == fullBot.inactivityTime) ?? null);
            setInactivityTimeClose(listInactivity.find(v => v.value == fullBot.inactivityTimeClose) ?? null);
            setTransferSOS(fullBot?.transferSOS?.agent_area ?? null);
            setTransferSOSType(fullBot?.transferSOS?.type ?? null);
            const currentTimeoutMinutes = fullBot?.obsoleteButtonTimeout
                ? Math.round(Number(fullBot.obsoleteButtonTimeout) / 60000)
                : obsoleteButtonTimeoutOptions[4].value;
            setObsoleteButtonTimeout(
                obsoleteButtonTimeoutOptions.find((opt) => opt.value === currentTimeoutMinutes) ??
                    obsoleteButtonTimeoutOptions[4]
            );
            const currentDebounceWindow = Number(fullBot?.debounceWindowMs);
            setDebounceWindowMs(
                Number.isFinite(currentDebounceWindow)
                    ? debounceWindowOptions.find((opt) => opt.value === currentDebounceWindow) ??
                          defaultDebounceWindow
                    : defaultDebounceWindow
            );
            setConfigBot(fullBot);

            setActiveSection('response');
            reset(objectData, { keepDefaultValues: true });
            setConfigModal(true);
        } catch (error) {
            console.error('Error al cargar configuración del bot:', error);
            sendToast({ type: 'error', message: 'No fue posible cargar la configuración del bot.' });
        } finally {
            setLoading(false);
        }
    };

    //# Function para guardar configuración
    const handleBotConfig = async (values) => {
        
        let json = {
          ...configBot,
          inactivityTime: inactivityTime?.value,
          inactivityTimeClose: inactivityTimeClose?.value,
          obsoleteButtonStatus: obsoleteButtonStatus,
          obsoleteButtonTimeout: (obsoleteButtonTimeout?.value ?? obsoleteButtonTimeoutOptions[4].value) * 60000,
          debounceWindowMs: debounceWindowMs?.value ?? defaultDebounceWindow.value,
          inactivityMessages: values.inactivityMessages,
          companySchedule: values.companySchedule,
          controlMessage: values.controlMessage,
          inactivityStatus: inactivityStatus,
          cordialityMessage: cordialityMessage,
          campaignFlows: configBot?.campaignFlows || false,
          attemptsSOS: attemptsSOS,
          waitingMessageSOS: values.waitingMessageSOS,
          transferSOS: {
            type: transferSOSType,
            agent_area: transferSOS
          },
          listSOSMessages: values.listSOSMessages,
          listExitIAMessages: values.listExitIAMessages,
          waitingMessageExitIA: values.waitingMessageExitIA,
          listCordialityMessages: values.listCordialityMessages,
          responseCordialityMessage: values.responseCordialityMessage
        };
    
        if(json.inactivityMessages){
          json.inactivityMessages = [{type: "text", text: {body: json.inactivityMessages}}];
        }
        
        setLoading(true);
        try {
            const url = urlAPI_3 + `chatBot/${json._id}`;
            const options = { method: "PUT", mode: "cors", body: JSON.stringify(json)};
            const response = await fetchWithAuth(url, options);
            if (response && response.ok === false) throw new Error(`HTTP ${response.status}`);
            await getBots(false);
            closeConfig();
            sendToast({type: "success", message: "Configuración guardada"});
        } catch (error) {
            console.error('Error guardando configuración:', error);
            sendToast({type: "error", message: "No fue posible guardar la configuración."});
        } finally {
            setLoading(false);
        }
    };

    //# Function BotIsActive
    const setBotEnabled = async (bot, enabled) => {
        setLoading(true);

        try {
            const fullBot = await getBotDetails(bot._id);
            if(!fullBot) {
                sendToast({type: "error", message: "No fue posible cargar la información del bot."});
                return;
            }

            if(enabled == false) {
                try {
                    const urlDelete = urlAPI_3 + 'chatBotStatuses/company';
                    const optionsDelete = { method: "DELETE", mode: "cors" };
                    await fetchWithAuth(urlDelete, optionsDelete);
                } catch (error) {
                    console.log('Error al limpiar estados:', error);
                }
            }

            let json = {
                ...fullBot,
                enabled: enabled
            }

            const url = urlAPI_3 + `chatBot/${json._id}`;
            const options = { method: "PUT", mode: "cors", body: JSON.stringify(json)};
            await fetchWithAuth(url, options);

            await getBots(false);
            sendToast({type: "success", message: enabled ? "Bot activado" : "Bot desactivado y conversaciones limpiadas"})
        } catch (error) {
            console.error('Error actualizando estado del bot:', error);
            sendToast({type: "error", message: "No fue posible cambiar el estado del bot."});
        } finally {
            setLoading(false);
        }
    };

    const handleToggleBot = (bot) => {
        if(!bot.enabled) {
            const validate = listBots.find(v => v.enabled == true && v._id !== bot._id)
            if(validate) {
                sendToast({type: "info", message: `Desactiva «${validate.name}» para poder activar este bot.`})
                return
            }
            setBotEnabled(bot, true)
            return
        }
        setConfirm({
            title: `Desactivar «${bot.name}»`,
            text: 'Se limpiarán las conversaciones activas de toda la empresa. Esto no se puede deshacer.',
            action: 'Desactivar y limpiar',
            run: () => setBotEnabled(bot, false),
        })
    };

    const runBotRequest = async (url, successMessage, errorMessage) => {
        setLoading(true);
        try {
            const response = await fetchWithAuth(url, { method: 'DELETE', mode: 'cors' });
            if (response && response.ok === false) throw new Error(`HTTP ${response.status}`);
            await getBots(false);
            sendToast({ type: 'success', message: successMessage });
        } catch (error) {
            console.error(error);
            sendToast({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    //# Function para limpiar estados del bot
    const handleClearBotStates = (bot) => {
        setConfirm({
            title: `Limpiar estados de «${bot.name}»`,
            text: 'Se eliminarán los estados activos y se reiniciarán las conversaciones del bot.',
            action: 'Limpiar estados',
            run: () => runBotRequest(urlAPI_3 + `chatBot/${bot._id}/clear-states`, 'Estados del bot limpiados', 'No fue posible limpiar los estados del bot.'),
        });
    };

    const handleDeleteBot = (bot) => {
        setConfirm({
            title: `Eliminar «${bot.name}»`,
            text: 'No podrás recuperarlo.',
            action: 'Eliminar bot',
            run: () => runBotRequest(urlAPI_3 + `chatBot/${bot._id}`, 'Bot eliminado', 'No fue posible eliminar el bot.'),
        });
    };
    

    useEffect(() => {
        handleSearch("");
    }, []);

    useEffect(() => {
        setByPage(12);
    }, [setByPage]);

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, typeFilter, setPage]);

    useEffect(() => {
        setActiveSection('response');
        setConfigSnapshot(configModal ? configSignature : null);
    }, [configModal]);

    useEffect(() => {
        if(mode == "create") {
            setSelectedTemplateModal(false);
        } 
    }, [mode])
    
    return (
        <>  
            {loading ? (
                <div className="content-spinner">
                <Spinner />
                </div>
            ) : null}

            <style>{SCROLL_CSS}</style>
            <style>{BOT_LIST_CSS}</style>
            <div ref={listRootRef} className="su-kit su-list-root d-flex flex-column" style={{ height: listRootHeight ? `${listRootHeight}px` : undefined }}>
                <div className="su-head">
                    <div className="col-xs-9 heading">
                        <div className="d-flex align-items-center" style={{ gap: "14px", minWidth: 0 }}>
                            <h1>Constructor de bots</h1>
                            {!validation && (
                                <span className="su-count">
                                    {visibleBots.length === listBots.length ? `${listBots.length} bot${listBots.length === 1 ? "" : "s"}` : `${visibleBots.length} de ${listBots.length} bots`}
                                </span>
                            )}
                        </div>
                        <div className="su-head-right">
                            {!validation && (
                                <>
                                    <div className="su-search">
                                        <LuSearch className="su-ic" size={17} />
                                        <input
                                            type="text"
                                            id="botListSearch"
                                            placeholder="Buscar bot por nombre"
                                            aria-label="Buscar bot por nombre"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <button type="button" className="su-btn" onClick={() => setSelectedTemplateModal(true)}>
                                        <LuPlus className="su-ic" size={16} />
                                        Nuevo bot
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    {!validation && (
                        <div className="su-chips su-filters">
                            {statusChips.map((chip) => (
                                <button
                                    key={chip.label}
                                    type="button"
                                    className={`su-chip${statusFilter === chip.value ? " on" : ""}`}
                                    style={{ "--c": chip.color }}
                                    aria-pressed={statusFilter === chip.value}
                                    onClick={() => setStatusFilter(statusFilter === chip.value ? null : chip.value)}
                                >
                                    <i />
                                    {chip.label}
                                </button>
                            ))}
                            <span className="su-chips-sep" />
                            {typeChips.map((chip) => (
                                <button
                                    key={chip.value}
                                    type="button"
                                    className={`su-chip${typeFilter === chip.value ? " on" : ""}`}
                                    style={{ "--c": chip.color }}
                                    aria-pressed={typeFilter === chip.value}
                                    onClick={() => setTypeFilter(typeFilter === chip.value ? null : chip.value)}
                                >
                                    <i />
                                    {chip.label}
                                </button>
                            ))}
                            {getAppliedFiltersCount() > 0 && (
                                <button type="button" className="su-link" onClick={clearFilters}>
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className={`su-board ${SCROLL_CLASS}`}>
                    {validation ? (
                        <NoBots onCreate={() => setSelectedTemplateModal(true)} />
                    ) : visibleBots.length === 0 && listBots.length > 0 ? (
                        <NoResults
                            search={search.trim()}
                            onClear={() => {
                                setSearch("");
                                clearFilters();
                            }}
                            onCreate={() => setSelectedTemplateModal(true)}
                        />
                    ) : (
                        <div className="su-grid">
                            {visibleBots.slice().reverse().sort((a, b) => Number(Boolean(b.enabled)) - Number(Boolean(a.enabled))).slice((page - 1) * byPage, (page - 1) * byPage + byPage).map((bot) => (
                                <BotListCard
                                    key={bot._id}
                                    bot={bot}
                                    onEdit={handlEditChatBot}
                                    onConfig={openConfig}
                                    onClearStates={handleClearBotStates}
                                    onDelete={handleDeleteBot}
                                    onToggle={handleToggleBot}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <ListPager total={visibleBots.length} page={page} byPage={byPage} setPage={setPage} />
            </div>

            {/*----------MODALS----------*/}

            {/* PANEL CONFIG BOT*/}
            <ConfigDrawer
                open={configModal}
                subtitle={configBot?.name || ""}
                dirty={configDirty}
                blocked={Boolean(confirm)}
                onClose={requestCloseConfig}
                onDiscard={closeConfig}
                onSubmit={handleSubmit(handleBotConfig)}
            >
                <ConfigSection id="response" active={activeSection} setActive={setActiveSection} icon={LuZap} color="#0a7ba5" title="Respuesta" summary={`Espera: ${debounceWindowMs?.label || "sin definir"} · Botones: ${obsoleteButtonStatus ? `vencen en ${obsoleteButtonTimeout?.label}` : "no vencen"}`}>
                    <div className="su-cf">
                        <span className="su-cf-label">Espera por mensajes seguidos</span>
                        <SuSelect aria-label="Espera por mensajes seguidos" value={debounceWindowMs} onChange={(option) => setDebounceWindowMs(option)} options={debounceWindowOptions} />
                        <span className="su-cf-hint">Cuando el cliente escribe, el bot espera este tiempo por si sigue enviando mensajes, para unirlos y responder una sola vez. A mayor tiempo, el bot tarda más en contestar. Desactívalo en bots que solo usan botones.</span>
                    </div>
                    <SwitchRow label="Expirar botones interactivos" hint="Pasado este tiempo, el botón queda marcado como obsoleto." checked={obsoleteButtonStatus} onChange={setObsoleteButtonStatus} />
                    <div className="su-cf">
                        <span className="su-cf-label">Tiempo de expiración</span>
                        <SuSelect aria-label="Tiempo de expiración" value={obsoleteButtonTimeout} onChange={(option) => setObsoleteButtonTimeout(option)} options={obsoleteButtonTimeoutOptions} />
                    </div>
                </ConfigSection>

                <ConfigSection id="inactivity" active={activeSection} setActive={setActiveSection} icon={LuClock} color="#e0a32e" title="Inactividad" summary={inactivityStatus ? `Activa · avisa a los ${inactivityTime?.label || "—"}, cierra a los ${inactivityTimeClose?.label || "—"}` : "Inactiva"}>
                    <SwitchRow label="Avisar por inactividad" checked={inactivityStatus} onChange={setInactivityStatus} />
                    <div className="su-cf">
                        <span className="su-cf-label">Enviar mensaje de inactividad a los</span>
                        <SuSelect aria-label="Tiempo para enviar mensaje de inactividad" value={inactivityTime} onChange={(option) => setInactivityTime(option)} options={listInactivity} />
                    </div>
                    <div className="su-cf">
                        <span className="su-cf-label">Cerrar la conversación a los</span>
                        <SuSelect
                            aria-label="Tiempo para cerrar la conversación"
                            value={inactivityTimeClose}
                            onChange={(option) => {
                                if(option.value > inactivityTime?.value) {
                                    setInactivityTimeClose(option);
                                }
                            }}
                            options={listInactivity}
                        />
                        <span className="su-cf-hint">Debe ser mayor que el tiempo del mensaje de inactividad.</span>
                    </div>
                    <div className="su-cf">
                        <label htmlFor="companySchedule_message">Mensaje de inactividad</label>
                        <textarea
                            className="su-cf-box"
                            id="companySchedule_message"
                            rows={3}
                            placeholder="Escribe el mensaje"
                            {...register("inactivityMessages", {
                                required: { value: true, message: "Escribe el mensaje de inactividad." },
                                pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                            })}
                        />
                        {errors.inactivityMessages && <span className="su-cf-err">{errors.inactivityMessages.message}</span>}
                        <span className="su-cf-hint">Mensaje de ausencia que se envía al cliente.</span>
                    </div>
                </ConfigSection>

                <ConfigSection id="messages" active={activeSection} setActive={setActiveSection} icon={LuMessageSquare} color="#2fae4e" title="Mensajes" summary={`Mensaje de control · Cordialidad ${cordialityMessage ? "activa" : "inactiva"}`}>
                    <div className="su-cf">
                        <label htmlFor="controlMessage_message">Mensaje de control</label>
                        <textarea
                            className="su-cf-box"
                            id="controlMessage_message"
                            rows={3}
                            placeholder="Escribe el mensaje"
                            {...register("controlMessage", {
                                required: { value: true, message: "Escribe el mensaje de control." },
                                pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                            })}
                        />
                        {errors.controlMessage && <span className="su-cf-err">{errors.controlMessage.message}</span>}
                        <span className="su-cf-hint">Se envía si la respuesta del cliente no es la que el bot espera.</span>
                    </div>
                    <SwitchRow label="Mensaje de cordialidad" checked={cordialityMessage} onChange={setCordialityMessage} />
                    <div className="su-cf">
                        <label htmlFor="list_friendly_messages">Palabras de cordialidad</label>
                        <textarea
                            className="su-cf-box"
                            id="list_friendly_messages"
                            rows={3}
                            placeholder="Gracias, Perfecto, Está bien, Excelente"
                            {...register("listCordialityMessages", {
                                required: { value: cordialityMessage, message: "Escribe al menos una palabra." },
                                pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                            })}
                        />
                        {errors.listCordialityMessages && <span className="su-cf-err">{errors.listCordialityMessages.message}</span>}
                        <span className="su-cf-hint">Sepáralas con comas.</span>
                    </div>
                    <div className="su-cf">
                        <label htmlFor="response_cordiality_message">Mensaje de respuesta</label>
                        <textarea
                            className="su-cf-box"
                            id="response_cordiality_message"
                            rows={3}
                            placeholder="Escribe el mensaje"
                            {...register("responseCordialityMessage", {
                                required: { value: cordialityMessage, message: "Escribe el mensaje de respuesta." },
                                pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                            })}
                        />
                        {errors.responseCordialityMessage && <span className="su-cf-err">{errors.responseCordialityMessage.message}</span>}
                        <span className="su-cf-hint">Se envía como respuesta a las palabras anteriores.</span>
                    </div>
                </ConfigSection>

                <ConfigSection id="human" active={activeSection} setActive={setActiveSection} icon={LuHeadphones} color="#f26b1d" title="Atención humana" summary={transferSOS ? `Transfiere a ${transferSOSType === "areas" ? "área" : "agente"}: ${transferSOS.label}` : "Sin destino de transferencia"}>
                    <div className="su-cf-tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={activeSOSTab === 'transfer'} className={activeSOSTab === 'transfer' ? "on" : ""} onClick={() => setActiveSOSTab('transfer')}>
                            <LuRepeat className="su-ic" size={15} />
                            Transferir
                        </button>
                        <button type="button" role="tab" aria-selected={activeSOSTab === 'exit'} className={activeSOSTab === 'exit' ? "on" : ""} onClick={() => setActiveSOSTab('exit')}>
                            <LuHome className="su-ic" size={15} />
                            Volver al menú
                        </button>
                    </div>
                    {activeSOSTab === 'transfer' && (
                        <>
                            <div className="su-cf">
                                <label htmlFor="list_sos_messages">Palabras para transferir</label>
                                <textarea
                                    className="su-cf-box"
                                    id="list_sos_messages"
                                    rows={2}
                                    placeholder="agente, humano, ayuda"
                                    {...register("listSOSMessages", {
                                        pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                                        validate: (value) => {
                                            const exitWords = watch('listExitIAMessages');
                                            const validation = validateDuplicateWords(value, exitWords);
                                            return validation.isValid || `Palabras duplicadas con "Volver al menú": ${validation.duplicates.join(', ')}`;
                                        }
                                    })}
                                />
                                {errors.listSOSMessages && <span className="su-cf-err">{errors.listSOSMessages.message}</span>}
                                <span className="su-cf-hint">Activan la transferencia a un agente. Sepáralas con comas.</span>
                            </div>
                            <div className="su-cf">
                                <span className="su-cf-label">Intentos antes de transferir</span>
                                <SuSelect aria-label="Intentos antes de transferir" value={attemptsSOS} onChange={(option) => setAttemptsSOS(option)} options={listAttempts} />
                            </div>
                            <div className="su-cf">
                                <span className="su-cf-label">Transferir a</span>
                                <div className="su-cf-seg">
                                    {[{ value: "areas", label: "Áreas" }, { value: "agents", label: "Agentes" }].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            aria-pressed={transferSOSType === option.value}
                                            className={transferSOSType === option.value ? "on" : ""}
                                            onClick={() => {
                                                setTransferSOSType(option.value);
                                                setTransferSOS(null);
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {transferSOSType === 'areas' && (
                                <div className="su-cf">
                                    <span className="su-cf-label">Área</span>
                                    <SuSelect aria-label="Área" value={transferSOS} onChange={(area) => setTransferSOS(area)} options={listAreas} />
                                </div>
                            )}
                            {transferSOSType === 'agents' && (
                                <div className="su-cf">
                                    <span className="su-cf-label">Agente</span>
                                    <SuSelect aria-label="Agente" value={transferSOS} onChange={(agent) => setTransferSOS(agent)} options={listAdvisers} />
                                </div>
                            )}
                            <div className="su-cf">
                                <label htmlFor="waiting_message_sos">Mensaje de transferencia</label>
                                <textarea
                                    className="su-cf-box"
                                    id="waiting_message_sos"
                                    rows={2}
                                    placeholder="Te conectamos con un agente..."
                                    {...register("waitingMessageSOS", {
                                        pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                                    })}
                                />
                                {errors.waitingMessageSOS && <span className="su-cf-err">{errors.waitingMessageSOS.message}</span>}
                                <span className="su-cf-hint">Se envía antes de transferir a un agente.</span>
                            </div>
                        </>
                    )}
                    {activeSOSTab === 'exit' && (
                        <>
                            <div className="su-cf">
                                <label htmlFor="list_exit_ia_messages">Palabras para volver al menú</label>
                                <textarea
                                    className="su-cf-box"
                                    id="list_exit_ia_messages"
                                    rows={2}
                                    placeholder="menu, bot, inicio, salir"
                                    {...register("listExitIAMessages", {
                                        pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                                        validate: (value) => {
                                            const sosWords = watch('listSOSMessages');
                                            const validation = validateDuplicateWords(sosWords, value);
                                            return validation.isValid || `Palabras duplicadas con "Transferir": ${validation.duplicates.join(', ')}`;
                                        }
                                    })}
                                />
                                {errors.listExitIAMessages && <span className="su-cf-err">{errors.listExitIAMessages.message}</span>}
                                <span className="su-cf-hint">Sacan al cliente del modo IA y lo llevan al menú principal. Sepáralas con comas.</span>
                            </div>
                            <div className="su-cf">
                                <label htmlFor="waiting_message_exit">Mensaje de regreso al menú</label>
                                <textarea
                                    className="su-cf-box"
                                    id="waiting_message_exit"
                                    rows={2}
                                    placeholder="Regresando al menú principal..."
                                    {...register("waitingMessageExitIA", {
                                        pattern: { value: /\S+/, message: "No puede ser solo espacios en blanco" },
                                    })}
                                />
                                {errors.waitingMessageExitIA && <span className="su-cf-err">{errors.waitingMessageExitIA.message}</span>}
                                <span className="su-cf-hint">Se envía antes de regresar al menú principal.</span>
                            </div>
                        </>
                    )}
                </ConfigSection>

                <ConfigSection id="agent" active={activeSection} setActive={setActiveSection} icon={LuSparkles} color="#7563D8" title="Agente IA" summary={configBot?.agentConfig?.enabled ? "Activo en este bot" : "Desactivado en este bot"}>
                    <SwitchRow label="Usar Agente IA en este bot" hint="Los mensajes del cliente los atiende la IA. Los eventos de webhook siguen por el flujo." checked={Boolean(configBot?.agentConfig?.enabled)} onChange={(enabled) => setAgentConfig({ enabled })} />
                    <div className="su-cf">
                        <label htmlFor="agent_company_name">Nombre de la marca</label>
                        <input
                            type="text"
                            id="agent_company_name"
                            className="su-cf-line"
                            value={configBot?.agentConfig?.companyName || ''}
                            onChange={(e) => setAgentConfig({ companyName: e.target.value })}
                            placeholder="Vacío = nombre de la cuenta"
                        />
                        <span className="su-cf-hint">Así se presenta la IA.</span>
                    </div>
                    <div className="su-cf-two">
                        <div className="su-cf">
                            <label htmlFor="agent_monthly_turns">Tope mensual (turnos)</label>
                            <input
                                type="number"
                                id="agent_monthly_turns"
                                min="0"
                                className="su-cf-line"
                                value={configBot?.agentConfig?.limits?.monthlyTurns ?? 0}
                                onChange={(e) => setAgentConfig({ limits: { ...(configBot?.agentConfig?.limits || {}), monthlyTurns: Number(e.target.value) || 0 } })}
                            />
                        </div>
                        <div className="su-cf">
                            <label htmlFor="agent_limit_message">Mensaje al llegar al tope</label>
                            <input
                                type="text"
                                id="agent_limit_message"
                                className="su-cf-line"
                                value={configBot?.agentConfig?.limits?.message || ''}
                                onChange={(e) => setAgentConfig({ limits: { ...(configBot?.agentConfig?.limits || {}), message: e.target.value } })}
                                placeholder="En este momento no podemos atenderte por aquí…"
                            />
                        </div>
                    </div>
                    <span className="su-cf-hint">Las instrucciones y las herramientas se configuran dentro del bot, en la pestaña Agente IA.</span>
                </ConfigSection>

                <ConfigSection id="advanced" active={activeSection} setActive={setActiveSection} icon={LuSlidersHorizontal} color="#8B8B8B" title="Avanzado" summary={configBot?.campaignFlows ? "Solo flujos de campaña" : "Todos los flujos"}>
                    <SwitchRow
                        label="Activar solo flujos de campañas"
                        checked={Boolean(configBot?.campaignFlows)}
                        onChange={(campaignFlows) => setConfigBot(prev => ({ ...prev, campaignFlows }))}
                    />
                </ConfigSection>
            </ConfigDrawer>

            <NewBotModal
                open={selectedTemplateModal}
                onClose={() => setSelectedTemplateModal(false)}
                isDark={isDark}
                templates={listBotsTemplates || []}
                existingNames={(listBots || []).map((bot) => bot.name)}
                scratchNodes={initialNodes}
                onCreate={async (payload) => {
                    setSelectedTemplateModal(false);
                    await handleCreateFromModal(payload);
                }}
                onGenerateAI={() => {
                    setSelectedTemplateModal(false);
                    handleModalBotAssistant(true);
                }}
            />

            <ConfirmDialog confirm={confirm} onClose={closeConfirm} />
        </>
    )
}

export default List;

